import React, { useState, useEffect } from 'react';
import {
    TextField, TablePagination, Grid, InputAdornment, IconButton, Paper, Typography, Skeleton, useMediaQuery,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Menu, MenuItem, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, Select, FormControl, InputLabel, CircularProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab'; // Import LoadingButton
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { IoSearchOutline } from "react-icons/io5";
import { Setting } from 'iconsax-react';
import { makeRequest } from '../../util/AxiosInstance';
import { formatDate, handleError } from '../../util/functions'; // Ensure handleError is imported
import { useTheme } from '@emotion/react';
import useDebounce from '../../components/Debounce';
import { toast } from 'sonner'; // Ensure toast is imported

// Define UserStatus enum based on backend values
const UserStatus = {
    INACTIVE: '0', // Corresponds to BANNED in backend enum
    ACTIVE: '1',   // Corresponds to NOTBANNED in backend enum
    SUSPENDED: '2' // Corresponds to SUSPENDED in backend enum (if needed)
};

const UserRow = ({ user, onActionClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        onActionClick(user, action);
        handleMenuClose();
    };

    const getStatusChip = (status) => {
        switch (status) {
            case UserStatus.ACTIVE: return <Chip label="Active" color="success" size="small" />;
            case UserStatus.INACTIVE: return <Chip label="Inactive" color="error" size="small" />;
            case UserStatus.SUSPENDED: return <Chip label="Suspended" color="warning" size="small" />;
            default: return <Chip label="Unknown" size="small" />;
        }
    };

    return (
        <>
            <TableRow key={user.id}>
                <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                <TableCell>{user.email}</TableCell>
                {!isMobile && <TableCell>{user.role_name || 'N/A'}</TableCell>} {/* Display role name */}
                {!isMobile && <TableCell>{getStatusChip(user.status)}</TableCell>}
                {!isMobile && <TableCell>{formatDate(user.created_at)}</TableCell>}
                <TableCell align="right">
                    <IconButton size="small" onClick={handleMenuClick}>
                        <Setting size="16" />
                    </IconButton>
                </TableCell>
            </TableRow>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => handleAction('change_role')}>Change Role</MenuItem>
                <MenuItem onClick={() => handleAction('toggle_status')}>Change Status</MenuItem>
                <MenuItem onClick={() => handleAction('delete_user')} sx={{ color: 'error.main' }}>Delete User</MenuItem>
                {/* Add more actions as needed */}
            </Menu>
        </>
    );
};

const Users = () => {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const debouncedSearch = useDebounce(search, 500);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [selectedUser, setSelectedUser] = useState(null);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete confirmation
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    // Fetch Users Query
    const fetchUsers = async (queryPage, queryPerPage, querySearch) => {
        // ... existing fetchUsers code ...
        try {
            const params = new URLSearchParams();
            params.append('page', queryPage + 1);
            params.append('per_page', queryPerPage);

            if (querySearch) {
                params.append('search', querySearch);
            }
            const response = await makeRequest('GET', `get-users?${params.toString()}`);
            if (response.data && response.data.data) {
                return response.data.data;
            } else {
                console.error("Unexpected API response structure:", response.data);
                return { data: [], total: 0, current_page: 1 };
            }
        } catch (error) {
            let msg = handleError(error);
            console.error("Failed to fetch users:", msg);
            toast.error("Failed to fetch users.");
            return { data: [], total: 0, current_page: 1 };
        }
    };

    const { data: usersData, isLoading: isLoadingUsers, isError: isErrorUsers, error: errorUsers, refetch } = useQuery({
        queryKey: ['users', page, rowsPerPage, debouncedSearch],
        queryFn: () => fetchUsers(page, rowsPerPage, debouncedSearch),
        keepPreviousData: true,
    });

    // Fetch Roles Query
    const fetchRoles = async () => {
        try {
            const response = await makeRequest('GET', 'get-all-user-roles');
            // Filter out 'agent' role if necessary based on user prompt
            return response.data.data.filter(role => role.name !== 'agent');
        } catch (error) {
            console.error("Failed to fetch roles:", error);
            toast.error("Failed to fetch roles.");
            return [];
        }
    };

    const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
        queryKey: ['roles'],
        queryFn: fetchRoles,
    });

    // --- Mutations ---

    // Change Role Mutation
    const changeRoleMutation = useMutation({
        mutationFn: ({ userId, roleId }) => makeRequest('PUT', `users/${userId}/role`, { role_id: roleId }),
        onSuccess: (data) => {
            toast.success(data?.data?.message || "User role updated successfully!");
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setIsRoleModalOpen(false);
        },
        onError: (error) => {
            // Get message from handleError and show toast
            const errorMessage = handleError(error, "Failed to change user role.");
            toast.error(errorMessage);
        },
    });

    // Change Status Mutation
    const changeStatusMutation = useMutation({
        mutationFn: ({ userId, status }) => makeRequest('PUT', `users/${userId}/status`, { status: status }),
        onSuccess: (data) => {
            toast.success(data?.data?.message || "User status updated successfully!");
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setIsStatusModalOpen(false);
        },
        onError: (error) => {
            // Get message from handleError and show toast
            const errorMessage = handleError(error, "Failed to change user status.");
            toast.error(errorMessage);
        },
    });

    // Delete User Mutation
    const deleteUserMutation = useMutation({
        mutationFn: (userId) => makeRequest('DELETE', `users/${userId}`), // Assumes DELETE /api/users/{userId} endpoint
        onSuccess: (data) => {
            toast.success(data?.data?.message || "User deleted successfully!");
            queryClient.invalidateQueries(['users']); // Refetch users list
            setIsDeleteModalOpen(false); // Close modal on success
            setSelectedUser(null); // Clear selected user
        },
        onError: (error) => {
            // Get message from handleError and show toast
            const errorMessage = handleError(error, "Failed to delete user.");
            toast.error(errorMessage);
            // Keep modal open on error? Or close? User preference. Let's close it for now.
            // setIsDeleteModalOpen(false);
        },
    });

    const handleSearchChange = (event) => {
        setPage(0);
        setSearch(event.target.value);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleUserAction = (user, action) => {
        setSelectedUser(user);
        if (action === 'change_role') {
            // Find the current role ID to pre-select in the dropdown
            const currentRole = rolesData?.find(r => r.name === user.role_name);
            setSelectedRoleId(currentRole?.id || '');
            setIsRoleModalOpen(true);
        } else if (action === 'toggle_status') {
            setSelectedStatus(user.status); // Pre-select current status
            setIsStatusModalOpen(true);
        } else if (action === 'delete_user') {
            setIsDeleteModalOpen(true); // Open delete confirmation modal
        }
    };

    const handleCloseModals = () => {
        setIsRoleModalOpen(false);
        setIsStatusModalOpen(false);
        setIsDeleteModalOpen(false); // Close delete modal as well
        setSelectedUser(null);
        setSelectedRoleId('');
        setSelectedStatus('');
    };

    const handleRoleChangeSubmit = () => {
        if (selectedUser && selectedRoleId) {
            changeRoleMutation.mutate({ userId: selectedUser.id, roleId: selectedRoleId });
        }
    };

    const handleStatusChangeSubmit = () => {
        if (selectedUser && selectedStatus) {
            changeStatusMutation.mutate({ userId: selectedUser.id, status: selectedStatus });
        }
    };

    const handleDeleteUserConfirm = () => {
        if (selectedUser) {
            deleteUserMutation.mutate(selectedUser.id);
        }
    };

    // --- Rendering ---

    const renderSkeleton = () => {
        // ... existing renderSkeleton code ...
        return Array.from({ length: rowsPerPage }).map((_, index) => (
            <TableRow key={index}>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell><Skeleton variant="text" /></TableCell>
                {!isMobile && <TableCell><Skeleton variant="text" /></TableCell>}
                {!isMobile && <TableCell><Skeleton variant="text" /></TableCell>}
                {!isMobile && <TableCell><Skeleton variant="text" /></TableCell>}
                <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
            </TableRow>
        ));
    };

    const usersList = usersData?.data ?? [];
    const totalUsers = usersData?.total ?? 0;

    if (isErrorUsers) {
        return <Typography color="error">Error fetching users: {errorUsers?.message ?? 'Unknown error'}</Typography>;
    }

    return (
        <>
            <Paper sx={{ padding: '1rem', borderRadius: '1rem' }}>
                {/* ... existing header and search ... */}
                <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} md={8}>
                        <Typography fontWeight={600} variant="h6">Manage Users</Typography>
                        <Typography variant="body2">View and manage user accounts</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            placeholder="Search users..."
                            value={search}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoSearchOutline />
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                        />
                    </Grid>
                </Grid>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                {!isMobile && <TableCell>Role</TableCell>} {/* Added Role column */}
                                {!isMobile && <TableCell>Status</TableCell>}
                                {!isMobile && <TableCell>Joined</TableCell>}
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingUsers ? renderSkeleton() : (
                                usersList.length > 0 ? usersList.map(user => (
                                    <UserRow key={user.id} user={user} onActionClick={handleUserAction} />
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={isMobile ? 3 : 6} align="center"> {/* Adjusted colSpan */}
                                            <Typography>No users found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalUsers}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>

            {/* Change Role Modal */}
            <Dialog open={isRoleModalOpen} onClose={handleCloseModals}>
                <DialogTitle>Change Role for {selectedUser?.email}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense" disabled={isLoadingRoles}>
                        <InputLabel id="role-select-label">Role</InputLabel>
                        <Select
                            labelId="role-select-label"
                            value={selectedRoleId}
                            label="Role"
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                        >
                            {isLoadingRoles && <MenuItem disabled><CircularProgress size={20} /></MenuItem>}
                            {rolesData?.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModals} disabled={changeRoleMutation.isPending}>Cancel</Button>
                    <LoadingButton
                        onClick={handleRoleChangeSubmit}
                        loading={changeRoleMutation.isPending}
                        variant="contained"
                        disabled={!selectedRoleId || isLoadingRoles}
                    >
                        Save Role
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Change Status Modal */}
            <Dialog open={isStatusModalOpen} onClose={handleCloseModals}>
                <DialogTitle>Change Status for {selectedUser?.email}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="status-select-label">Status</InputLabel>
                        <Select
                            labelId="status-select-label"
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <MenuItem value={UserStatus.ACTIVE}>Active</MenuItem>
                            <MenuItem value={UserStatus.INACTIVE}>Inactive</MenuItem>
                            {/* Add Suspended if needed */}
                            {/* <MenuItem value={UserStatus.SUSPENDED}>Suspended</MenuItem> */}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModals} disabled={changeStatusMutation.isPending}>Cancel</Button>
                    <LoadingButton
                        onClick={handleStatusChangeSubmit}
                        loading={changeStatusMutation.isPending}
                        variant="contained"
                        disabled={!selectedStatus}
                    >
                        Save Status
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={isDeleteModalOpen} onClose={handleCloseModals}>
                <DialogTitle sx={{ color: 'error.main' }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to permanently delete the user{' '}
                        <strong>{selectedUser?.email}</strong>? This action cannot be undone.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will also delete associated data and update related records.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModals} disabled={deleteUserMutation.isPending}>Cancel</Button>
                    <LoadingButton
                        onClick={handleDeleteUserConfirm}
                        loading={deleteUserMutation.isPending}
                        variant="contained"
                        color="error" // Use error color for delete button
                    >
                        Delete User
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Users;